"use client";

import { BarChart3, Users, Layers, Landmark, Building2, Download } from "lucide-react";
import {
  BRANCHES, RELATIONSHIP_MANAGERS, BROKING_SEGMENTS, rmById, branchById,
  formatINRCompact, formatINR, segmentMeta, type BrokingLead,
} from "@/lib/demos/stockBrokingData";

interface StockBrokingReportsTabProps {
  leads: BrokingLead[];
}

function Bar({ label, value, max, sub, color = "from-emerald-500 to-teal-500" }: { label: string; value: number; max: number; sub: string; color?: string }) {
  const pct = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-slate-600 font-medium truncate">{label}</span>
        <span className="text-slate-500 font-mono shrink-0 ml-2">{sub}</span>
      </div>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function StockBrokingReportsTab({ leads }: StockBrokingReportsTabProps) {
  // Leads by RM
  const leadsByRm = RELATIONSHIP_MANAGERS.map((rm) => ({
    rm,
    count: leads.filter((l) => l.rm_id === rm.id).length,
    active: leads.filter((l) => l.rm_id === rm.id && l.stage === "Active Trader").length,
  })).sort((a, b) => b.count - a.count);
  const maxLeads = Math.max(1, ...leadsByRm.map((r) => r.count));

  // Conversion by segment (active accounts holding the segment)
  const segStats = BROKING_SEGMENTS.map((seg) => ({
    seg,
    count: leads.filter((l) => l.segments_active.includes(seg.id)).length,
  })).sort((a, b) => b.count - a.count);
  const maxSeg = Math.max(1, ...segStats.map((s) => s.count));

  // AUM by RM
  const aumByRm = RELATIONSHIP_MANAGERS.map((rm) => ({
    rm,
    aum: leads.filter((l) => l.rm_id === rm.id).reduce((s, l) => s + l.aum, 0),
  })).sort((a, b) => b.aum - a.aum);
  const maxAum = Math.max(1, ...aumByRm.map((r) => r.aum));

  // Brokerage by branch
  const brokByBranch = BRANCHES.map((b) => ({
    branch: b,
    brokerage: leads.filter((l) => l.branch_id === b.id).reduce((s, l) => s + l.brokerage_mtd, 0),
    clients: leads.filter((l) => l.branch_id === b.id).length,
  })).sort((a, b) => b.brokerage - a.brokerage);
  const maxBrok = Math.max(1, ...brokByBranch.map((r) => r.brokerage));

  function exportCsv() {
    const headers = ["Client Code", "Name", "City", "Branch", "RM", "Stage", "Segments", "AUM", "Brokerage MTD", "SIP/mo"];
    const rows = leads.map((l) => [
      l.client_code, l.name, l.city, branchById(l.branch_id)?.name ?? "", rmById(l.rm_id)?.name ?? "",
      l.stage, l.segments_active.map((s) => segmentMeta(s)?.short).join(" | "),
      l.aum, l.brokerage_mtd, l.sip_active ? l.sip_amount : 0,
    ]);
    const csv = "﻿" + [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `broking_mis_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" /> MIS &amp; Reporting
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Branch &amp; RM performance analytics across the account-opening funnel.</p>
        </div>
        <button
          onClick={exportCsv}
          className="self-start sm:self-auto py-2 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 text-xs font-semibold flex items-center gap-1.5 transition-colors"
        >
          <Download className="w-3.5 h-3.5 text-emerald-600" /> Export MIS (CSV)
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Leads by RM */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-emerald-600" /> Leads by Relationship Manager</h3>
          <div className="space-y-3">
            {leadsByRm.map(({ rm, count, active }) => (
              <Bar key={rm.id} label={`${rm.name} · ${branchById(rm.branchId)?.city}`} value={count} max={maxLeads} sub={`${count} leads · ${active} active`} />
            ))}
          </div>
        </div>

        {/* Conversion by segment */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4"><Layers className="w-4 h-4 text-violet-600" /> Active Accounts by Segment</h3>
          <div className="space-y-3">
            {segStats.map(({ seg, count }) => (
              <Bar key={seg.id} label={`${seg.label} · ${seg.exchange}`} value={count} max={maxSeg} sub={`${count} clients`} color="from-violet-500 to-fuchsia-500" />
            ))}
          </div>
        </div>

        {/* AUM by RM */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4"><Landmark className="w-4 h-4 text-blue-600" /> AUM by Relationship Manager</h3>
          <div className="space-y-3">
            {aumByRm.map(({ rm, aum }) => (
              <Bar key={rm.id} label={rm.name} value={aum} max={maxAum} sub={formatINRCompact(aum)} color="from-blue-500 to-sky-500" />
            ))}
          </div>
        </div>

        {/* Brokerage by branch */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4"><Building2 className="w-4 h-4 text-amber-600" /> Brokerage Revenue by Branch (MTD)</h3>
          <div className="space-y-3">
            {brokByBranch.map(({ branch, brokerage, clients }) => (
              <Bar key={branch.id} label={`${branch.name}`} value={brokerage} max={maxBrok} sub={`${formatINR(brokerage)} · ${clients} clients`} color="from-amber-500 to-orange-500" />
            ))}
          </div>
        </div>
      </div>

      <p className="text-[10px] text-slate-500 text-center">
        Demo MIS computed live from in-memory mock clients. In production these tiles query the trading back-office / RMS and settlement systems.
      </p>
    </div>
  );
}
